import { useState, useEffect, type ReactNode } from 'react';
import type { CatalogState } from '../hooks/useCatalog';

export type Route =
  | '/'
  | '/dashboard'
  | '/profile'
  | '/cart'
  | '/admin'
  | '/admin/sub-clients'
  | `/admin/sub-clients/${string}`
  | '/admin/subscriptions'
  | '/admin/orders'
  | '/admin/audit-log'
  | '/admin/sandbox-provisioning'
  | '/sandbox/request'
  | `/apps/${string}/operations`
  | '/preview/loaded'
  | '/preview/loading'
  | '/preview/no-results'
  | '/preview/no-access'
  | '/preview/fetch-failed';

const PREVIEW_STATE_MAP: Partial<Record<Route, CatalogState['tag']>> = {
  '/preview/loaded': 'loaded',
  '/preview/loading': 'loading',
  '/preview/no-results': 'empty-no-results',
  '/preview/no-access': 'empty-no-access',
  '/preview/fetch-failed': 'empty-fetch-failed',
};

interface RouterContextValue {
  route: Route;
  navigate: (r: Route) => void;
  previewState?: CatalogState['tag'];
}

import { createContext, useContext } from 'react';

const RouterContext = createContext<RouterContextValue>({
  route: '/',
  navigate: () => {},
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const getRoute = (): Route => {
    const hash = window.location.hash.replace('#', '') || '/';
    const path = hash.split('?')[0].replace(/#test-org-search.*$/, '').trim();
    return (path || '/') as Route;
  };

  const [route, setRouteState] = useState<Route>(getRoute);

  useEffect(() => {
    const handler = () => setRouteState(getRoute());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  function navigate(r: Route) {
    window.location.hash = r;
    setRouteState(r);
  }

  const previewState = PREVIEW_STATE_MAP[route];

  return (
    <RouterContext.Provider value={{ route, navigate, previewState }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  return useContext(RouterContext);
}
